import React from 'react';

// Design System Components
import Skeleton from 'Components/DesignSystem/Skeleton/Skeleton';

// Constants
import { STRINGS } from '../../constants';

/**
 * The editor's first load, holding the editor's own shape (2026-09-16).
 *
 * It replaces a 21px DS `Spinner` alone in a 1240x703 blank region — 0.3% of the
 * area — which held no geometry at all: the header and all three columns landed
 * in one jump when the card arrived. Measured in Chrome against a held request.
 *
 * **Why not `PageSkeleton`.** Design-system § 62 puts a page's whole first load in
 * tier 3, but its presets are `list` / `detail` / `summary-list` and none of them
 * is a three-column editor. That section's own rule is that "a preset that does not
 * match its page is worse than no skeleton" — a `detail` form in front of this page
 * would draw five stacked fields that never arrive. So this composes the tier-1
 * `Skeleton` primitive (the only owner of the shimmer recipe) into THIS page's
 * shape. If a second three-column editor ever wants it, promote it to a preset
 * rather than copying it — that is what § 62 asks for.
 *
 * Geometry is taken from `EditorBody`, not guessed: the same row classes, the same
 * 320px side columns (`EXPANDED_WIDTH_CLASS`), the same elastic middle. The
 * announcement is a single `role="status"` and the shapes are `aria-hidden`, which
 * is the DS rule — skeletons say nothing, exactly one live region speaks.
 *
 * **Every shape is sized by the `width`/`height` PROPS, never by a `tw-w-*` /
 * `tw-h-*` class.** `Skeleton` writes `width: 100%` inline whenever no `width` prop
 * is given, and an inline style beats any class — so a classed width loses silently.
 * The first cut used classes and was measured in Chrome: each field row's 16px
 * checkbox shape rendered **288px wide** and pushed its row bar to 0.
 */

/** `EditorBody`'s row and column classes, verbatim — so nothing moves on arrival. */
const ROW_CLASS =
  'tw-relative tw-flex tw-flex-wrap md:tw-flex-nowrap tw-items-stretch tw-min-w-0 md:tw-flex-1 md:tw-min-h-0 md:tw-overflow-clip';
const SIDE_COLUMN_CLASS = 'tw-shrink-0 tw-overflow-clip tw-w-full md:tw-w-80';
const PREVIEW_CLASS = 'tw-flex-1 tw-min-w-0 tw-p-4 md:tw-px-8 md:tw-h-full md:tw-overflow-y-auto';

/** The pane lists ~9 field rows before it scrolls; each is a 36px control row. */
const FIELD_ROWS = 9;
/** The properties column opens on the card's own properties: a heading and 4 rows. */
const PANEL_ROWS = 4;

const EditorSkeleton = () => (
  <>
    {/* The one voice. `Skeleton` itself is silent, and the shapes below are hidden,
        so this sentence is everything a screen-reader user is told while it waits. */}
    <span role="status" className="tw-sr-only" data-testid="card-editor-skeleton-status">
      {STRINGS.editor.loading}
    </span>

    <div className={ROW_CLASS} aria-busy="true" data-testid="card-editor-skeleton">
      {/* The configuration pane: its search field, its FIELDS heading, its rows.
          `wave={index}` staggers the sweep so the column reads as ONE light moving
          across it rather than nine strips blinking together — the same offset
          `Table` gives its skeleton rows. */}
      <div className={SIDE_COLUMN_CLASS} aria-hidden="true" data-testid="card-editor-skeleton-pane">
        <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
          <Skeleton variant="rect" height="36px" />
          <Skeleton variant="rect" width="64px" height="16px" />
          <div className="tw-flex tw-flex-col tw-gap-2">
            {Array.from({ length: FIELD_ROWS }, (_unused, index) => (
              <div key={index} className="tw-flex tw-items-center tw-gap-3">
                <Skeleton
                  variant="rect"
                  width="16px"
                  height="16px"
                  className="tw-shrink-0"
                  wave={index}
                />
                {/* The elastic half is a flex wrapper holding a 100%-wide shape —
                    the Skeleton itself never carries the flex class. */}
                <div className="tw-flex-1 tw-min-w-0">
                  <Skeleton variant="rect" height="36px" wave={index} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* The preview: the card box the storefront simulation lands in, centred at the
          width the real one occupies — photo region, two info lines, the CTA. */}
      <div className={PREVIEW_CLASS} data-testid="card-editor-skeleton-preview" aria-hidden="true">
        <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[320px] tw-flex-col tw-gap-3 tw-pt-8">
          <Skeleton variant="rect" height="192px" />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="rect" height="40px" />
        </div>
      </div>

      {/* The properties column: its heading, then label-over-control pairs. */}
      <div className={SIDE_COLUMN_CLASS} aria-hidden="true" data-testid="card-editor-skeleton-panel">
        <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
          <Skeleton variant="rect" width="128px" height="20px" />
          {Array.from({ length: PANEL_ROWS }, (_unused, index) => (
            <div key={index} className="tw-flex tw-flex-col tw-gap-2">
              <Skeleton variant="text" width="45%" wave={index} />
              <Skeleton variant="rect" height="36px" wave={index} />
            </div>
          ))}
        </div>
      </div>
    </div>
  </>
);

export default EditorSkeleton;
